import type { PropsWithChildren, ReactElement } from 'react';
import type { StripeTitle } from '@george-labs.com/design-system';
import { useEffect } from 'react';
import { Stepper } from '@george-labs.com/design-system';

import type { Props as FocusPageProps } from '../focus-page/FocusPage';
import type { ContentWrapper, ShouldHaveChildrenProp, StepperConfiguration } from './types';
import { FocusPage } from '../focus-page/FocusPage';
import { useCurrentStepId } from './hooks/useCurrentStepId';
import { getSteps } from './FocusPageWithStepperService';

interface Props<StepId, ContentWrapperProps extends ShouldHaveChildrenProp>
    extends Omit<FocusPageProps, 'title' | 'stepper'> {
    /** Fallback title, if title in the step configuration isn't defined */
    title?: string | ReactElement<typeof StripeTitle>;
    /**
     * Stepper configuration is defined as a array of steps.
     * Every step in configuration represent unique "screen" of focus page, NOT a label in Stepper component ("circle").
     * "Circles" are defined by a property stepIndex, so more "screens" can belong to one "circle".
     * Label under the "circle" is taken from a property stepLabel. If there is more "screens" for "circle",
     * label is defined by the first stepLabel property in configuration array or is overridden by stepLabel from current step
     *
     * Default step is the first step of the configuration!
     *
     * Labels under the "circles" must be unique strings!
     * */
    stepperConfiguration: StepperConfiguration<StepId>[];
    /** Do not show component defined in step - it's useful for e.g. loading spinner, etc. */
    showStepperConfigurationComponent?: boolean;
    /** Wrap content of focus page into the defined component with optional props. Component has to contain children prop */
    contentWrapper?: ContentWrapper<ContentWrapperProps>;
    /** Initial step id */
    initialStepId?: StepId;
}

export const FocusPageWithStepper = <StepId, ContentWrapperProps extends ShouldHaveChildrenProp>({
    children,
    title = '',
    stepperConfiguration,
    showStepperConfigurationComponent = true,
    contentWrapper,
    initialStepId,
    ...props
}: PropsWithChildren<Props<StepId, ContentWrapperProps>>): React.ReactNode => {
    const { currentStepId, dispatchActions } = useCurrentStepId();
    const currentStepIdTyped = currentStepId as unknown as StepId;
    const ContentWrapperComponent = contentWrapper?.component;

    useEffect(() => {
        const defaultStep = initialStepId ?? stepperConfiguration[0]?.stepId;
        dispatchActions.setCurrentStepId(defaultStep as unknown as string);

        return () => {
            dispatchActions.resetCurrentStepId();
        };
    }, [initialStepId]);

    const steps = getSteps(stepperConfiguration, currentStepIdTyped);
    const currentStep = stepperConfiguration.find(({ stepId }) => stepId === currentStepIdTyped);

    if (__DEV__) {
        const uniqueSteps = [...new Set(steps)];

        if (steps.length !== uniqueSteps.length) {
            throw Error("Array of steps shouldn't contain duplicated values");
        }
    }

    const showStepper = currentStep?.stepIndex !== false;
    const stepper = showStepper && (
        <Stepper
            steps={steps}
            currentStepIndex={currentStep?.stepIndex || 0}
            data-cy={`shared-focus-page-ui-${currentStepId}`}
        />
    );

    const content = (
        <>
            {children}
            {showStepperConfigurationComponent && currentStep?.component}
        </>
    );

    const wrappedContent = ContentWrapperComponent && (
        <ContentWrapperComponent {...(contentWrapper.props as ContentWrapperProps)}>
            {content}
        </ContentWrapperComponent>
    );

    return (
        <FocusPage title={currentStep?.title || title} stepper={stepper || undefined} {...props}>
            {wrappedContent || content}
        </FocusPage>
    );
};

FocusPageWithStepper.SIZE = FocusPage.SIZE;
